import { Controller } from "@hotwired/stimulus"
import { sessionPresent } from "../lib/session_marker"

const STORAGE_KEY = "bookmark-manager:state:v1"

export default class extends Controller {
  static targets = ["control"]
  static values = {
    url: String
  }

  initialize() {
    this.ready = false
    this.loading = null
    this.savedBookmarks = new Map()
    this.authenticated = false
    this.complete = false
    this.csrfToken = null
    this.busyKeys = new Set()

    if (sessionPresent()) {
      this.restoreStoredState()
    } else {
      this.clearStoredState()
    }
  }

  connect() {
    if (!this.hasControlTarget || this.ready) return

    if (sessionPresent()) {
      this.ensureLoaded().catch(() => {})
    } else {
      this.loadAnonymousState()
    }
  }

  controlTargetConnected(control) {
    if (this.ready) {
      this.renderControl(control)
    } else if (sessionPresent()) {
      this.ensureLoaded().catch(() => {})
    } else {
      this.loadAnonymousState()
    }
  }

  async toggle(event) {
    event.preventDefault()

    const control = event.currentTarget

    if (!this.ready) {
      try {
        await this.ensureLoaded()
      } catch {
        return
      }
    }

    if (this.isBusy(control)) return

    if (!this.authenticated) {
      this.openModal(control.dataset.bookmarkAuthPath)
      return
    }

    if (!this.complete) {
      this.openModal(control.dataset.bookmarkCompletionPath)
      return
    }

    if (this.savedBookmarks.has(control.dataset.bookmarkKey)) {
      await this.unsave(control)
    } else {
      await this.save(control)
    }
  }

  async ensureLoaded() {
    if (!this.loading) {
      this.loading = this.loadState().catch((error) => {
        this.loading = null
        throw error
      })
    }

    return this.loading
  }

  loadAnonymousState() {
    this.authenticated = false
    this.complete = false
    this.csrfToken = null
    this.savedBookmarks.clear()
    this.ready = true
    this.clearStoredState()
    this.controlTargets.forEach((control) => this.renderControl(control))
  }

  applyState(state) {
    this.authenticated = state.authenticated
    this.complete = state.complete
    this.csrfToken = state.csrf_token
    this.savedBookmarks = new Map(state.saved_bookmarks.map((bookmark) => [bookmark.key, bookmark.id]))
    this.ready = true
    this.controlTargets.forEach((control) => this.renderControl(control))
  }

  restoreStoredState() {
    try {
      const storedState = JSON.parse(sessionStorage.getItem(STORAGE_KEY))
      const valid = storedState?.authenticated === true &&
        typeof storedState.complete === "boolean" &&
        typeof storedState.csrf_token === "string" &&
        Array.isArray(storedState.saved_bookmarks) &&
        storedState.saved_bookmarks.every((bookmark) => typeof bookmark.key === "string" && bookmark.id != null)

      if (!valid) {
        this.clearStoredState()
        return
      }

      this.applyState(storedState)
    } catch {
      this.clearStoredState()
    }
  }

  storeState() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        authenticated: this.authenticated,
        complete: this.complete,
        csrf_token: this.csrfToken,
        saved_bookmarks: Array.from(this.savedBookmarks, ([key, id]) => ({ key, id }))
      }))
    } catch {
      // Storage may be unavailable; the next page will sync from Rails again.
    }
  }

  clearStoredState() {
    try {
      sessionStorage.removeItem(STORAGE_KEY)
    } catch {
      // Storage may be unavailable; there is no cached state to clear.
    }
  }

  async loadState() {
    const response = await fetch(this.urlValue, {
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    })

    if (!response.ok) throw new Error("Unable to load bookmark state")

    const state = await response.json()
    this.applyState(state)

    if (this.authenticated) {
      this.storeState()
    } else {
      this.clearStoredState()
    }
  }

  async save(control) {
    const key = control.dataset.bookmarkKey
    this.setBusyForKey(key, true)

    try {
      const response = await this.mutate(this.urlValue, "POST", {
        bookmark: this.bookmarkParams(control)
      })

      if (await this.handleMutationRedirect(response, control)) return
      if (!response.ok) return

      const data = await response.json()
      this.savedBookmarks.set(data.key, data.id)
      this.storeState()
      this.renderMatchingControls(data.key)
    } finally {
      this.setBusyForKey(key, false)
    }
  }

  async unsave(control) {
    const key = control.dataset.bookmarkKey
    const bookmarkId = this.savedBookmarks.get(key)
    if (!bookmarkId) return

    this.setBusyForKey(key, true)

    try {
      const response = await this.mutate(this.destroyUrlFor(bookmarkId), "DELETE")

      if (await this.handleMutationRedirect(response, control)) return

      if (response.ok || response.status === 404) {
        this.savedBookmarks.delete(key)
        this.storeState()
        this.renderMatchingControls(key)
      }
    } finally {
      this.setBusyForKey(key, false)
    }
  }

  destroyUrlFor(bookmarkId) {
    // Expects urlValue to be a RESTful collection URL, e.g. /bookmarks.
    return `${this.urlValue.replace(/\/$/, "")}/${encodeURIComponent(bookmarkId)}`
  }

  async mutate(url, method, body = null) {
    let response = await this.fetchMutation(url, method, body)

    if (response.status === 422) {
      await this.refreshState()
      response = await this.fetchMutation(url, method, body)
    }

    return response
  }

  async refreshState() {
    this.loading = null
    this.clearStoredState()
    return this.ensureLoaded()
  }

  async fetchMutation(url, method, body = null) {
    return fetch(url, {
      method,
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      body: body ? JSON.stringify(body) : null
    })
  }

  async handleMutationRedirect(response, control) {
    if (response.status !== 401 && response.status !== 403) return false

    const data = await response.json()
    this.authenticated = data.authenticated
    this.complete = data.complete

    if (this.authenticated) {
      this.storeState()
    } else {
      this.clearStoredState()
    }

    this.openModal(data.auth_path || data.completion_path || control.dataset.bookmarkAuthPath)
    return true
  }

  bookmarkParams(control) {
    return {
      bookmarkable_type: control.dataset.bookmarkableType,
      bookmarkable_id: control.dataset.bookmarkableId,
      return_to: control.dataset.bookmarkReturnTo
    }
  }

  renderMatchingControls(key) {
    this.controlTargets
      .filter((control) => control.dataset.bookmarkKey === key)
      .forEach((control) => this.renderControl(control))
  }

  setBusyForKey(key, busy) {
    if (busy) {
      this.busyKeys.add(key)
    } else {
      this.busyKeys.delete(key)
    }

    this.controlTargets
      .filter((control) => control.dataset.bookmarkKey === key)
      .forEach((control) => this.renderBusyState(control))
  }

  isBusy(control) {
    return this.busyKeys.has(control.dataset.bookmarkKey)
  }

  renderControl(control) {
    if (!this.ready) return

    const saved = this.savedBookmarks.has(control.dataset.bookmarkKey)
    control.setAttribute("aria-pressed", saved.toString())
    control.classList.toggle("text-primary", saved)
    this.renderBusyState(control)

    control.querySelector('[data-bookmark-icon="saved"]')?.classList.toggle("hidden", !saved)
    control.querySelector('[data-bookmark-icon="unsaved"]')?.classList.toggle("hidden", saved)
  }

  renderBusyState(control) {
    const busy = !this.ready || this.isBusy(control)
    control.disabled = busy

    if (busy) {
      control.setAttribute("aria-busy", "true")
    } else {
      control.removeAttribute("aria-busy")
    }
  }

  openModal(path) {
    const frame = document.getElementById("modal-content")
    if (!frame || !path) return

    frame.src = path
  }
}
